import { Provider, TypedInMemoryProvider } from '@openfeature/web-sdk';

/**
 * Flag defaults for the fully-local stack.
 *
 * These are NOT new policy. They are exactly what the app already falls back to when ConfigCat is
 * unreachable, restated as a real provider so the local stack never has to reach ConfigCat's CDN to
 * arrive at them:
 *
 *   • the three boolean flags — FeatureFlagService.isFeatureEnabled$() passes `defaultValue = false`
 *     at every call site, so an unreachable ConfigCat already yields false.
 *   • corinsights_maintenance_banner — getMaintenanceBannerConfig$() maps an empty string to
 *     `{ enabled: false }`, and ConfigCatHealthService's degraded path (`healthy === false`) forces
 *     the same disabled config regardless of the flag value.
 *
 * Stated explicitly rather than left to FLAG_NOT_FOUND fallback so that local behaviour is
 * deterministic and greppable, and so a developer can exercise a flag locally by flipping one
 * `defaultVariant` here instead of pointing the local SPA at a live SaaS tenant.
 *
 * Keys must stay in lockstep with FEATURE_FLAGS in feature-flag.service.ts.
 */
export const LOCAL_FEATURE_FLAGS = {
  corinsights_delete_field_data: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  corinsights_maintenance_report_columns: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  // Enabled for the LOCAL stack only: this provider is selected solely when configCatSdkKey is
  // empty (the hermetic local build), so deployed envs still resolve this flag from ConfigCat.
  // Turns on the Data Processing metadata edit-request accept/decline UI so a survey with pending
  // metadata changes can be reviewed and published end-to-end locally.
  corinsights_edit_metadata: {
    variants: { on: true, off: false },
    defaultVariant: 'on',
    disabled: false,
  },
  corinsights_api_auth_enabled: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  // SYRC-7347: lazy, node-based move-asset picker (and the Move row action). Off by default, locally too. It
  // is on only where corinsights_hierarchy_phase1 is also on. To try it on the local stack, set defaultVariant
  // to 'on' here and for phase 1 below, and run the node generator for the company: a company with no Node
  // rows gets a "move destinations are not available" message, not the existing tree.
  corinsights_lazy_hierarchy_picker: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  // SYRC-7533: hierarchy phase 1 rollout switch. Off by default, locally too. To try phase 1 on the local
  // stack, set defaultVariant to 'on' here (or, without a rebuild, the LOCAL_FLAG_OVERRIDES_KEY override
  // below) and FEATURE_CORINSIGHTS_HIERARCHY_PHASE1=true for the lambdas.
  corinsights_hierarchy_phase1: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  // SYRC-7561: advanced asset template settings. Off by default, locally too.
  corinsights_advanced_template_settings: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
  // String flag: '' is the documented "banner off" value (see getMaintenanceBannerConfig$).
  corinsights_maintenance_banner: {
    variants: { off: '' },
    defaultVariant: 'off',
    disabled: false,
  },
  // Auth0 provider kill-switch. This is the SAME flag key real environments read via ConfigCat —
  // this in-memory variant is just what OpenFeature.setProvider() gets wired up to for the fully-local
  // stack (see createFeatureFlagProvider() in app.module.ts, selected by an empty configCatSdkKey).
  // auth-config.module.ts's authConfigLoaderFactory() (via resolve-auth0-enabled.ts) reads it off the
  // OpenFeature client lazily, the same way for both local and real builds — flip this flag's
  // defaultVariant to switch between corview/auth0 locally.
  corinsights_auth0_enabled: {
    variants: { on: true, off: false },
    defaultVariant: 'off',
    disabled: false,
  },
} as const;

/**
 * An inert, fully offline OpenFeature provider for the local stack.
 *
 * WHY THIS EXISTS. environment.local.ts used to ship a real ConfigCat SaaS SDK key, so
 * `ConfigCatWebProvider.create(...)` in AppModule dialled ConfigCat's CDN at boot even on a stack
 * that is otherwise completely hermetic — a live third-party network call during every local run and
 * every e2e run. That is the last non-hermetic runtime dependency in the local SPA.
 *
 * Selection is by EMPTY `environment.configCatSdkKey`, not by `environment.name`: environment.ts (the
 * default, staging-pointing environment) also carries `name: 'LOCAL'`, so the name is not a reliable
 * discriminator. An absent key is — there is nothing to authenticate with, so there is nothing to
 * call.
 */
export function createLocalFeatureFlagProvider(
  overrides: Record<string, unknown> = readLocalFlagOverrides(),
): Provider {
  const flags: Record<string, (typeof LOCAL_FEATURE_FLAGS)[keyof typeof LOCAL_FEATURE_FLAGS]> = {
    ...LOCAL_FEATURE_FLAGS,
  };
  for (const [key, value] of Object.entries(overrides)) {
    const flag = flags[key];
    if (!flag || typeof value !== 'boolean' || !('on' in flag.variants)) continue;
    flags[key] = { ...flag, defaultVariant: value ? 'on' : 'off' } as typeof flag;
  }
  return new TypedInMemoryProvider(flags as typeof LOCAL_FEATURE_FLAGS);
}

/**
 * SYRC-7558: localStorage key holding boolean flag overrides for the local stack, as JSON, e.g.
 * `{"corinsights_hierarchy_phase1": true}`. Read once when the app starts, so set it before loading the
 * page (the e2e specs do this with an init script). Only this offline provider reads it: a deployed build
 * has a ConfigCat key and never creates this provider. Unknown keys and non-boolean values are ignored.
 */
export const LOCAL_FLAG_OVERRIDES_KEY = 'syrc.localFlagOverrides';

function readLocalFlagOverrides(): Record<string, unknown> {
  try {
    const raw = globalThis.localStorage?.getItem(LOCAL_FLAG_OVERRIDES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
